import { Flex, Input, useColorMode, useToast } from '@chakra-ui/react';
import {
  abovePurchaseProfitThreshold,
  evaluateProfit,
  profitTextColor,
  setValueAsDecimalExtender,
  validateNotNaN,
} from '../shared';
import { useContext } from 'react';
import { SalesContext } from '../Sales';
import { useTranslation } from 'react-i18next';
import {
  AdditionalCharge,
  AutoUpdateAdditionalCharge,
  additionalChargeApi,
} from '@texas/api/endpoints/additionalChargeApi';
import { AutoUpdateResponseCallback } from '@texas/components/shared/autoUpdate/types';
import {
  useAutoUpdateForm,
  AutoUpdateWrapper,
} from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import {
  exchangeRateCalculation,
  round,
} from '@texas/utils/helpers/numberHelpers';
import { MarginCalculation } from '../MarginCalculation';
import { CustomerCurrency } from '@texas/api/endpoints/currenciesApi';
import { PurchaseInformation } from '@texas/api/endpoints/purchaseInformationApi';
import { SalesCurrency } from '@texas/api/endpoints/salesCurrenciesApi';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import useFormat from '@texas/hooks/useFormat';

export interface AdditionalChargeFormProps {
  additionalCharge: AdditionalCharge;
  purchaseInformation?: PurchaseInformation;
  salesCurrency: SalesCurrency;
  isDisabled: boolean;
  selectedCurrency: CustomerCurrency;
  sellingExchangeRate: number;
  decimalsInPrice: number;
}

export function AdditionalChargeForm(props: AdditionalChargeFormProps) {
  const salesContext = useContext(SalesContext);
  const { t } = useTranslation();
  const { numberFormat } = useFormat();
  const { colorMode } = useColorMode();
  const toast = useToast();

  const onResponse = (
    data: AutoUpdateResponseCallback<
      AdditionalCharge,
      AutoUpdateAdditionalCharge
    >,
  ) => {
    const toSet =
      salesContext?.salesInformation.data?.map((s) => {
        const salesCurrencies = s.currencies.map((c) => {
          if (c.id === props.salesCurrency.id) {
            c.additionalCharge = {
              ...c.additionalCharge,
              ...data.mappedServerData,
            };
            return c;
          }
          return c;
        });

        s.currencies = salesCurrencies;
        return s;
      }) ?? [];

    salesContext?.salesInformation.set(toSet);
  };

  const {
    register,
    registerAutoUpdate,
    watch,
    formState: { errors },
    form,
    triggerSubmit,
    setValue,
    getValues,
  } = useAutoUpdateForm<AutoUpdateAdditionalCharge, AdditionalCharge>({
    defaultValue: {
      mouldCharge: props.additionalCharge.mouldCharge,
      sampleCharge: props.additionalCharge.sampleCharge,
      surcharge: props.additionalCharge.surcharge,
    },
    requestParams: [props.salesCurrency.id],
    namePrefix: 'additionalCharge',
    autoUpdateRequest: additionalChargeApi.autoUpdateAdditionalCharge,
    onResponse,
  });

  const [mouldCharge, sampleCharge, surcharge] = watch([
    'mouldCharge',
    'sampleCharge',
    'surcharge',
  ]);

  const surchargeProfit = evaluateProfit(
    exchangeRateCalculation(
      surcharge,
      props.salesCurrency.fixedExchangeRate,
      props.sellingExchangeRate,
      props.decimalsInPrice,
    ),
    exchangeRateCalculation(
      props.purchaseInformation?.surcharge ?? 0,
      props.purchaseInformation?.fixedExchangeRate ?? 0,
      props.sellingExchangeRate,
      props.decimalsInPrice,
    ),
    abovePurchaseProfitThreshold,
  );
  const mouldChargeProfit = evaluateProfit(
    exchangeRateCalculation(
      mouldCharge,
      props.salesCurrency.fixedExchangeRate,
      props.sellingExchangeRate,
      props.decimalsInPrice,
    ),
    exchangeRateCalculation(
      props.purchaseInformation?.mouldCharge ?? 0,
      props.purchaseInformation?.fixedExchangeRate ?? 0,
      props.sellingExchangeRate,
      props.decimalsInPrice,
    ),
    abovePurchaseProfitThreshold,
  );
  const sampleChargeProfit = evaluateProfit(
    exchangeRateCalculation(
      sampleCharge,
      props.salesCurrency.fixedExchangeRate,
      props.sellingExchangeRate,
      props.decimalsInPrice,
    ),
    exchangeRateCalculation(
      props.purchaseInformation?.sampleCharge ?? 0,
      props.purchaseInformation?.fixedExchangeRate ?? 0,
      props.sellingExchangeRate,
      props.decimalsInPrice,
    ),
    abovePurchaseProfitThreshold,
  );

  return (
    <form {...form}>
      <fieldset disabled={props.isDisabled}>
        <Flex gap={2} alignItems="start">
          <TexasInputWrapper
            bottomLabel={`${numberFormat(
              exchangeRateCalculation(
                props.purchaseInformation?.sampleCharge ?? 0,
                props.purchaseInformation?.fixedExchangeRate ?? 0,
                props.sellingExchangeRate,
                props.decimalsInPrice,
              ),
              props.decimalsInPrice,
            )} ${props.selectedCurrency.code}`}
            label={t('variant.sales.sampleCharge')}
            rightAddon={props.salesCurrency.currencyCode}
            error={errors.sampleCharge}
            profit={sampleChargeProfit}
            utilButtons={
              <MarginCalculation
                purchasePrice={props.purchaseInformation?.sampleCharge ?? 0}
                purchaseExchangeRate={
                  props.purchaseInformation?.fixedExchangeRate ?? 0
                }
                salesPrice={sampleCharge}
                salesExchangeRate={props.salesCurrency.fixedExchangeRate}
                targetExchangeRate={props.selectedCurrency.buyingRate}
                targetCurrencyCode={props.selectedCurrency.code}
                profit={sampleChargeProfit}
                decimalsInPrice={props.decimalsInPrice}
              />
            }
          >
            <AutoUpdateWrapper
              autoUpdateProps={registerAutoUpdate}
              path="sampleCharge"
            >
              <Input
                variant="texas"
                color={profitTextColor(
                  sampleChargeProfit,
                  colorMode === 'dark',
                )}
                {...register('sampleCharge', {
                  required: true,
                  min: { value: 0, message: t('errors.notNegative') },
                  validate: (value) => validateNotNaN(value, t),
                  setValueAs: (value) =>
                    setValueAsDecimalExtender(value, false),
                  onBlur: () => {
                    const value = getValues('sampleCharge');
                    const rounded = round(value, props.decimalsInPrice);
                    if (rounded !== value) {
                      toast({
                        title: t('general.valueRounded'),
                        status: 'info',
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                    setValue('sampleCharge', rounded);
                    triggerSubmit();
                  },
                })}
                placeholder="0"
              />
            </AutoUpdateWrapper>
          </TexasInputWrapper>
          <TexasInputWrapper
            bottomLabel={`${numberFormat(
              exchangeRateCalculation(
                props.purchaseInformation?.surcharge ?? 0,
                props.purchaseInformation?.fixedExchangeRate ?? 0,
                props.sellingExchangeRate,
                props.decimalsInPrice,
              ),
              props.decimalsInPrice,
            )} ${props.selectedCurrency.code}`}
            label={t('variant.sales.surcharge')}
            rightAddon={props.salesCurrency.currencyCode}
            error={errors.surcharge}
            profit={surchargeProfit}
            utilButtons={
              <MarginCalculation
                purchasePrice={props.purchaseInformation?.surcharge ?? 0}
                purchaseExchangeRate={
                  props.purchaseInformation?.fixedExchangeRate ?? 0
                }
                salesPrice={surcharge}
                salesExchangeRate={props.salesCurrency.fixedExchangeRate}
                targetExchangeRate={props.selectedCurrency.buyingRate}
                targetCurrencyCode={props.selectedCurrency.code}
                profit={surchargeProfit}
                decimalsInPrice={props.decimalsInPrice}
              />
            }
          >
            <AutoUpdateWrapper
              autoUpdateProps={registerAutoUpdate}
              path="surcharge"
            >
              <Input
                variant="texas"
                color={profitTextColor(surchargeProfit, colorMode === 'dark')}
                {...register('surcharge', {
                  required: true,
                  min: { value: 0, message: t('errors.notNegative') },
                  validate: (value) => validateNotNaN(value, t),
                  onBlur: () => {
                    const value = getValues('surcharge');
                    const rounded = round(value, props.decimalsInPrice);
                    if (rounded !== value) {
                      toast({
                        title: t('general.valueRounded'),
                        status: 'info',
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                    setValue('surcharge', rounded);
                    triggerSubmit();
                  },
                  setValueAs: (value) =>
                    setValueAsDecimalExtender(value, false),
                })}
                placeholder="0"
              />
            </AutoUpdateWrapper>
          </TexasInputWrapper>
          <TexasInputWrapper
            bottomLabel={`${numberFormat(
              exchangeRateCalculation(
                props.purchaseInformation?.mouldCharge ?? 0,
                props.purchaseInformation?.fixedExchangeRate ?? 0,
                props.sellingExchangeRate,
                props.decimalsInPrice,
              ),
              props.decimalsInPrice,
            )} ${props.selectedCurrency.code}`}
            label={t('variant.sales.mouldCharge')}
            rightAddon={props.salesCurrency.currencyCode}
            error={errors.mouldCharge}
            profit={mouldChargeProfit}
            utilButtons={
              <MarginCalculation
                purchasePrice={props.purchaseInformation?.mouldCharge ?? 0}
                purchaseExchangeRate={
                  props.purchaseInformation?.fixedExchangeRate ?? 0
                }
                salesPrice={mouldCharge}
                salesExchangeRate={props.salesCurrency.fixedExchangeRate}
                targetExchangeRate={props.selectedCurrency.buyingRate}
                targetCurrencyCode={props.selectedCurrency.code}
                profit={mouldChargeProfit}
                decimalsInPrice={props.decimalsInPrice}
              />
            }
          >
            <AutoUpdateWrapper
              autoUpdateProps={registerAutoUpdate}
              path="mouldCharge"
            >
              <Input
                variant="texas"
                color={profitTextColor(mouldChargeProfit, colorMode === 'dark')}
                {...register('mouldCharge', {
                  required: true,
                  min: { value: 0, message: t('errors.notNegative') },
                  validate: (value) => validateNotNaN(value, t),
                  setValueAs: (value) =>
                    setValueAsDecimalExtender(value, false),
                  onBlur: () => {
                    const value = getValues('mouldCharge');
                    const rounded = round(value, props.decimalsInPrice);
                    if (rounded !== value) {
                      toast({
                        title: t('general.valueRounded'),
                        status: 'info',
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                    setValue('mouldCharge', rounded);
                    triggerSubmit();
                  },
                })}
                placeholder="0"
              />
            </AutoUpdateWrapper>
          </TexasInputWrapper>
        </Flex>
      </fieldset>
    </form>
  );
}
